import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { CheckboxField } from '@/components/ui/checkbox-field';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FieldsetGroup } from '@/components/ui/fieldset-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PERMISSION_RIGHT_FIELDS } from '@/constants/permissions';
import type {
  PermissionEntity,
  PermissionRightKey,
  PermissionRights,
} from '@/types/permissions';
import EntityCombobox from './EntityCombobox';
import { entityLabel } from './usePermissionManagement';

export interface PermissionDialogProps {
  open: boolean;
  isEditing: boolean;
  entities: PermissionEntity[];
  selectedEntity: PermissionEntity | null;
  onSelectedEntityChange: (entity: PermissionEntity | null) => void;
  rights: PermissionRights;
  onToggleRight: (right: PermissionRightKey) => (checked: boolean) => void;
  onSave: () => void;
  onClose: () => void;
  saving?: boolean;
  error?: string | null;
}

const PermissionDialog: React.FC<PermissionDialogProps> = ({
  open,
  isEditing,
  entities,
  selectedEntity,
  onSelectedEntityChange,
  rights,
  onToggleRight,
  onSave,
  onClose,
  saving = false,
  error = null,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent className="revy-ui sm:max-w-3xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="revy-cjk text-lg leading-[1.5]">
            {isEditing ? t('permissions.editPermission') : t('permissions.addPermission')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6">
          {error ? (
            <Alert variant="destructive">
              <AlertDescription className="revy-cjk text-sm leading-[1.5]">
                {error}
              </AlertDescription>
            </Alert>
          ) : null}

          {!isEditing && (
            <EntityCombobox
              label={t('permissions.selectEntity', {
                type: t('permissions.types.user').toLowerCase(),
              })}
              options={entities}
              value={selectedEntity}
              getOptionLabel={(entity) => entityLabel(entity, 'user')}
              onChange={onSelectedEntityChange}
              clearLabel={t('common.clear')}
            />
          )}

          <FieldsetGroup legend={t('documents.permissions')}>
            {PERMISSION_RIGHT_FIELDS.map((right) => (
              <CheckboxField
                key={right.key}
                checked={rights[right.key]}
                onChange={onToggleRight(right.key)}
                label={t(right.descriptionKey)}
              />
            ))}
          </FieldsetGroup>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onSave} disabled={saving || (!isEditing && !selectedEntity)}>
            {isEditing ? t('common.update') : t('common.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PermissionDialog;
